/**
 * SETUP CHECKLIST
 *
 * Onboarding banner shown below TopNavbar guiding new users through
 * 4 setup steps. Minimizable, dismissible, persisted via onboardingStore.
 */

import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { CheckCircle, Circle, ChevronUp, ChevronDown, X } from "lucide-react";
import {
  useOnboardingStore,
  useShouldShowChecklist,
  type OnboardingStepId,
} from "@/stores/onboardingStore";
import { cn } from "@/lib/utils";

const STEP_SUBTITLES: Record<OnboardingStepId, string> = {
  create_workspace: "Choose between a cloud or local workspace",
  create_project: "Click the + button in the top bar",
  add_task: "Open a project and create your first task",
  run_agent: "Click Start on a task to run the AI agent",
  review_result: "Merge or reject an agent's work",
};

export function SetupChecklist() {
  const visible = useShouldShowChecklist();
  const {
    steps,
    isMinimized,
    isCompleted,
    minimize,
    expand,
    dismiss,
    setRequestCreateWorkspace,
    setRequestAddProject,
  } = useOnboardingStore();
  const [, setLocation] = useLocation();
  const [showComplete, setShowComplete] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  // All-done celebration: show message then auto-dismiss
  useEffect(() => {
    if (isCompleted) {
      setShowComplete(true);
      const timer = setTimeout(() => {
        setDismissing(true);
        setTimeout(() => dismiss(), 500);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isCompleted, dismiss]);

  if (!visible && !showComplete) return null;

  const completedCount = steps.filter((s) => s.completed).length;
  const activeStep = steps.find((s) => !s.completed);

  // Completion animation
  if (showComplete) {
    return (
      <div className={cn(
        "bg-muted/50 border-b border-border/30 px-4 py-2.5 text-sm text-center text-emerald-500 font-medium",
        "transition-all duration-500",
        dismissing ? "opacity-0 max-h-0 py-0 overflow-hidden" : "opacity-100 max-h-12"
      )}>
        You're all set!
      </div>
    );
  }

  const handleStepClick = (stepId: OnboardingStepId) => {
    if (stepId !== activeStep?.id) return;
    switch (stepId) {
      case "create_workspace":
        setRequestCreateWorkspace(true);
        break;
      case "create_project":
        setRequestAddProject(true);
        break;
      case "add_task":
      case "run_agent":
        setLocation("/projects");
        break;
      case "review_result":
        setLocation("/");
        break;
    }
  };

  // Minimized view
  if (isMinimized) {
    return (
      <div className="bg-muted/50 border-b border-border/30 px-4 py-2 text-sm flex items-center transition-all duration-200">
        <span className="font-medium text-foreground">Getting started</span>
        <span className="ml-2 text-muted-foreground">
          {completedCount}/{steps.length}
        </span>
        <div className="flex-1" />
        <button
          onClick={expand}
          className="p-0.5 rounded hover:bg-muted-foreground/10 text-muted-foreground"
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  // Expanded view
  return (
    <div className="bg-muted/50 border-b border-border/30 px-4 py-2.5 text-sm transition-all duration-200">
      {/* Header row */}
      <div className="flex items-center mb-1.5">
        <span className="font-medium text-foreground">Getting started</span>
        <div className="flex-1" />
        <button
          onClick={minimize}
          className="p-0.5 rounded hover:bg-muted-foreground/10 text-muted-foreground mr-1"
        >
          <ChevronUp className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={dismiss}
          className="p-0.5 rounded hover:bg-muted-foreground/10 text-muted-foreground"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Steps row */}
      <div className="flex items-center gap-4">
        {steps.map((step) => {
          const isActive = step.id === activeStep?.id;
          const isPending = !step.completed && !isActive;

          return (
            <button
              key={step.id}
              onClick={() => handleStepClick(step.id)}
              disabled={!isActive}
              className={`flex items-center gap-1.5 transition-colors ${
                isActive
                  ? "cursor-pointer hover:text-blue-400"
                  : "cursor-default"
              }`}
            >
              {step.completed ? (
                <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
              ) : isActive ? (
                <span className="relative flex h-3.5 w-3.5 items-center justify-center flex-shrink-0">
                  <span className="absolute inline-flex h-2.5 w-2.5 rounded-full bg-blue-400 opacity-75 animate-pulse" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500" />
                </span>
              ) : (
                <Circle className="w-3.5 h-3.5 text-muted-foreground/50 flex-shrink-0" />
              )}
              <span
                className={
                  step.completed
                    ? "line-through text-muted-foreground"
                    : isActive
                      ? "font-medium text-foreground"
                      : isPending
                        ? "text-muted-foreground/50"
                        : ""
                }
              >
                {step.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Active step subtitle */}
      {activeStep && (
        <p className="mt-1 text-muted-foreground text-xs">
          {STEP_SUBTITLES[activeStep.id]}
        </p>
      )}
    </div>
  );
}
